<template>
  <transition name="p-message" mode="out-in">
    <div
      v-if="panelVisible"
      id="free-times-panel"
      class="notification-panel p-shadow-4 p-m-2 p-p-2"
      role="region"
      aria-label="Técnicos sem marcações"
    >
      <div class="notification-header p-d-flex p-jc-between p-ai-center p-mb-1">
        <div class="p-d-flex p-ai-center">
          <i
            class="pi pi-clock p-mr-2"
            style="font-size: 1.1rem; color: #f59e0b"
          ></i>
          <span class="p-text-bold" style="color: #4b5563; font-size: 1rem"
            >Técnicos sem marcações</span
          >
        </div>
        <div class="notification-actions">
          <Button
            icon="pi pi-times"
            class="p-button-rounded p-button-text p-button-secondary p-button-sm compact-btn"
            style="width: 2rem; height: 2rem"
            aria-label="Fechar"
            @click="close"
            v-tooltip.left="'Fechar'"
          />
        </div>
      </div>

      <div class="notification-body">
        <Loading :active="isFetching && !freeTimes.data.length" :isFullPage="false" />
        <p v-if="!isFetching && !freeTimes.data.length" class="p-m-2 p-text-secondary">
          Todos os técnicos têm marcações.
        </p>
        <div class="p-grid p-ai-center p-nogutter">
        <div
          v-for="time in freeTimes.data"
          :key="`${time.user}-${time.office}`"
          class="p-col-12 p-md-6 p-lg-4 p-xl-3 p-p-1"
        >
          <div
            class="tech-slot-card compact"
            :style="{ borderLeft: `4px solid ${getOfficeColor(time.office)}` }"
          >
            <div class="tech-header p-d-flex p-jc-between p-ai-center">
              <div class="tech-name">
                <i class="pi pi-user p-mr-1" style="font-size: 0.8rem"></i>
                <span class="p-text-truncate">{{ time.user }}</span>
              </div>
              <div
                class="compact-badge-custom"
                v-if="time.office"
                :style="getOfficeStyle(time.office)"
              >
                <i
                  class="pi pi-map-marker p-mr-1"
                  style="font-size: 0.6rem"
                ></i>
                {{ time.office }}
              </div>
            </div>
            <div class="time-chips">
              <span
                v-for="slot in time.freeSlots"
                :key="`${time.user}-${slot.start}-${slot.end}`"
                class="time-chip compact-chip"
              >
                {{ slot.start }} - {{ slot.end }}
              </span>
            </div>
          </div>
        </div>
        </div>
      </div>

      <div class="p-d-flex p-jc-end p-mt-1">
        <Button
          v-if="unread"
          label="Marcar como lido"
          icon="pi pi-check"
          class="p-button-sm p-button-text p-button-secondary p-mr-2 compact-action-btn"
          @click="markRead"
        />
        <Button
          label="Ir para a agenda"
          icon="pi pi-calendar"
          class="p-button-sm p-button-text p-button-primary compact-action-btn"
          @click="goToSupportMap($event)"
          @auxclick="goToSupportMap($event)"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import notificationsService from "@/services/notifications.service";
import { openInternalFromEvent } from "@/helpers/tabLinks";

const OFFICE_STYLES = {
  Faro: { backgroundColor: "#f59e0b", color: "#fff" },
  Lisboa: { backgroundColor: "#3b82f6", color: "#fff" },
  Porto: { backgroundColor: "#10b981", color: "#fff" },
};

const OFFICE_COLORS = {
  Faro: "#f59e0b",
  Lisboa: "#3b82f6",
  Porto: "#10b981",
};

export default {
  name: "NotificationsBell",
  props: {
    forceOpen: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    /**
     * Technicians arrived that nobody has marked read yet. They show as a
     * count on the top bar's clock; the panel itself opens only on demand.
     */
    unread: false,
    freeTimes: {
      id: null,
      data: [],
    },
    /** The notification the user marked read; it never lights the badge again. */
    readId: null,
    timer: null,
    isFetching: false,
    pollIntervalMs: 5 * 60 * 1000,
    lastPayloadSignature: "",
  }),
  computed: {
    /**
     * Only when asked for (the clock on the top bar): opening on its own,
     * seconds after the page, it covered or pushed the page's content.
     */
    panelVisible() {
      return this.forceOpen;
    },

    unreadCount() {
      return this.unread ? this.freeTimes.data.length : 0;
    },
  },
  watch: {
    /**
     * Opening shows what was fetched. A forced fetch would make the API
     * create a new, unread notification on every look, bringing the badge
     * back right after «Marcar como lido».
     */
    forceOpen(val) {
      if (val && !this.freeTimes.data.length) {
        this.getFreeTimes();
      }
    },
    unreadCount: {
      immediate: true,
      handler(count) {
        this.$emit("update-count", count);
      },
    },
    "$store.state.auth.status.loggedIn"(val) {
      if (val) {
        this.getFreeTimes();
      }
    },
  },
  created() {
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.scheduleNextFetch(5000);
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.clearScheduledFetch();
  },
  methods: {
    clearScheduledFetch() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
    scheduleNextFetch(delay = this.pollIntervalMs) {
      this.clearScheduledFetch();
      this.timer = setTimeout(() => {
        this.getFreeTimes();
      }, delay);
    },
    onVisibilityChange() {
      if (document.hidden) {
        this.clearScheduledFetch();
        return;
      }

      this.scheduleNextFetch(1000);
    },
    getPayloadSignature(data) {
      return JSON.stringify(
        data.map((entry) => ({
          user: entry.user,
          office: entry.office,
          freeSlots: (entry.freeSlots || []).map(
            (slot) => `${slot.start}-${slot.end}`
          ),
        }))
      );
    },
    async getFreeTimes(force = false) {
      if (this.isFetching) {
        this.scheduleNextFetch();
        return;
      }

      if (
        !this.$store.state.auth.status.loggedIn ||
        !this.can("seeFreeTimesSupport")
      ) {
        this.unread = false;
        this.freeTimes = { id: null, data: [] };
        this.lastPayloadSignature = "";
        this.scheduleNextFetch(10 * 60 * 1000);
        return;
      }

      this.isFetching = true;
      try {
        const response = await notificationsService.getFreeTimes(force);
        if (response && response.notModified) {
          if (!document.hidden) {
            this.scheduleNextFetch();
          }
          return;
        }
        const responseData =
          response && Array.isArray(response.data) ? response.data : [];
        // Read less than 15 minutes ago, the API answers with nothing; the
        // list the user just read stays, as read.
        if (!responseData.length && !(response && response.id) && this.readId) {
          this.unread = false;
          if (!document.hidden) {
            this.scheduleNextFetch();
          }
          return;
        }
        const sortedData = responseData
          .slice()
          .sort((a, b) => (a.office || "").localeCompare(b.office || ""));
        const signature = this.getPayloadSignature(sortedData);

        if (
          signature !== this.lastPayloadSignature ||
          this.freeTimes.id !== (response?.id || null)
        ) {
          this.freeTimes = {
            id: response?.id || null,
            data: sortedData,
          };
          this.lastPayloadSignature = signature;
        }

        this.unread =
          sortedData.length > 0 && (response?.id || null) !== this.readId;
      } catch (error) {
        console.error("Error fetching free times:", error);
      } finally {
        this.isFetching = false;
      }

      if (!document.hidden) {
        this.scheduleNextFetch();
      }
    },
    close() {
      this.$emit("close");
    },
    markRead() {
      this.unread = false;
      this.readId = this.freeTimes.id;
      this.$emit("close");
      if (this.freeTimes && this.freeTimes.id) {
        return notificationsService
          .freeTimesRead(this.freeTimes.id)
          .catch((err) => console.error(err));
      }
    },
    async goToSupportMap(event) {
      if (event.type === "auxclick" && event.button !== 1) return;
      if (event.type === "auxclick") event.preventDefault();
      await this.markRead();
      openInternalFromEvent(this, { path: `/support` }, event);
    },
    getOfficeStyle(office) {
      if (!office) return {};
      return (
        OFFICE_STYLES[office] || { backgroundColor: "#6b7280", color: "#fff" }
      );
    },
    getOfficeColor(office) {
      if (!office) return "#e5e7eb";
      return OFFICE_COLORS[office] || "#6b7280";
    },
  },
};
</script>

<style scoped>
/* In the page's flow; it only opens on a click, so nothing moves unasked. */
.notification-panel {
  background-color: #fffbeb; /* Amber-50 equivalent */
  border-left: 6px solid #f59e0b;
  border-radius: 4px;
  margin-top: 1rem !important;
  margin-bottom: 1.25rem !important;
}

.notification-body {
  position: relative;
  min-height: 60px;
}

.tech-slot-card {
  background: white;
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tech-slot-card.compact {
  padding: 0.25rem 0.5rem;
  min-height: auto;
}

.tech-name {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
}

.tech-slot-card.compact .tech-name {
  font-size: 0.8rem;
  margin-bottom: 0px;
}

.compact-badge-custom {
  font-size: 0.65rem;
  height: 1.1rem;
  line-height: normal;
  padding: 0 0.4rem;
  border-radius: 4px;
  display: flex !important;
  align-items: center;
  font-weight: 700;
  min-width: 1.5rem;
  justify-content: center;
}

.time-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.time-chip {
  background-color: #e0f2fe;
  color: #0369a1;
  font-size: 0.75rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  white-space: nowrap;
}

.time-chip.compact-chip {
  font-size: 0.8rem;
  padding: 1px 4px;
  font-weight: 600;
}

.compact-btn .p-button-icon {
  font-size: 0.8rem;
}

.compact-action-btn .p-button-label {
  font-size: 0.85rem;
}

/* Transition styles */
.p-message-enter-active,
.p-message-leave-active {
  transition: all 0.3s ease;
}
.p-message-enter,
.p-message-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
